import { useRouter } from "next/router";
import React from "react";
import {
  BreadcrumbContainer,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbNav,
  BreadcrumbSeparator,
} from "./Breadcrumb.style";

interface BreadcrumbProps {
  className?: string;
  hostname?: string;
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({
  className,
  hostname: propHostname,
}) => {
  const router = useRouter();

  // Get current path and hostname
  const currentPath = router.asPath;
  // Remove query parameters to get clean pathname
  const pathname = currentPath.split("?")[0];
  const hostname =
    propHostname ||
    (typeof window !== "undefined" ? window.location.hostname : "");

  const generateBreadcrumbs = () => {
    const breadcrumbs = [];

    // Always add Home as first breadcrumb
    breadcrumbs.push({
      label: "Home",
      href: `https://${hostname}/`,
      isActive: false,
    });

    // Check if we're on travel insurance page
    if (pathname.includes("/travel-insurance")) {
      // Check if there's a country in the path
      const countryMatch = pathname.match(/\/travel-insurance\/([^/?]+)/);
      const isOnCountryPage = countryMatch && countryMatch[1];
      const isOnTravelInsurancePage =
        pathname === "/travel-insurance" || pathname === "/travel-insurance/";

      breadcrumbs.push({
        label: "Travel Insurance",
        href: isOnTravelInsurancePage
          ? null
          : `https://${hostname}/travel-insurance/`, // No link if already on travel insurance page
        isActive: isOnTravelInsurancePage, // Active only if on main travel insurance page
      });

      // Add country breadcrumb if on country page
      if (isOnCountryPage) {
        const countrySlug = countryMatch[1];
        // Convert slug to readable country name (capitalize first letter)
        const countryName =
          countrySlug.charAt(0).toUpperCase() + countrySlug.slice(1);

        breadcrumbs.push({
          label: countryName,
          href: null, // No link for current page
          isActive: true,
        });
      }
    }

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  if (breadcrumbs.length <= 1) {
    return null; // Don't show breadcrumb if only home exists
  }

  return (
    <BreadcrumbNav aria-label="Breadcrumb" className={className}>
      <BreadcrumbContainer>
        {breadcrumbs.map((breadcrumb, index) => (
          <React.Fragment key={index}>
            <BreadcrumbItem
              isActive={breadcrumb.isActive}
              aria-current={breadcrumb.isActive ? "page" : undefined}
            >
              {breadcrumb.href ? (
                <BreadcrumbLink
                  href={breadcrumb.href}
                  onClick={(e) => {
                    e.preventDefault();
                    if (breadcrumb.href) {
                      window.location.href = breadcrumb.href;
                    }
                  }}
                >
                  {breadcrumb.label}
                </BreadcrumbLink>
              ) : (
                breadcrumb.label
              )}
            </BreadcrumbItem>
            {index < breadcrumbs.length - 1 && (
              <BreadcrumbSeparator aria-hidden="true">&gt;</BreadcrumbSeparator>
            )}
          </React.Fragment>
        ))}
      </BreadcrumbContainer>
    </BreadcrumbNav>
  );
};

export default Breadcrumb;
